import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Card, Typography, Tooltip } from "@mui/material";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

import { apiConstants, getGeneric } from "../../../common/constants";
import { getAccess } from "../../../helpers/ReduxHelpers";
import Loader from "../../../common/Loader";

import DetailsTab from "./details";
import RateCard from "./rateCard";
import KamDetails from "./kamDetails";
import Remittance from "../../cod/Remittance";
import ShipmentDetails from "./shipments";
import Configs from "./configuration";
import NotificationPricing from "./notificationPricing";
import ViewInvoice from "./invoices";
import ViewLostAndDamageOrders from "./lostAndDamageList";
import NotificationsSubscriptions from "./notifications";
import Rules from "../Rules";
import AllDiscrepancy from "../../Weight Discrepancy/AllDiscrepancy";
import RuleHistory from "../../uploads/WeightZoneRulesBuilder/RuleHistory";
import ClaimsSettings from "./claimsSettings";
import OnboardingTab from "./OnboardingTab";
import { NetraConfig } from "../NetraConfig";
import ViewSku from "./skuMaster";
import RoutingAndPincodes from "../../WebHooks/MerchantConfig";

import { C, RADIUS, SHADOW, SX, TYPE } from "../shared/tokens";
import AllocationEnginePanel from "../shared/AllocationEnginePanel";
import { merchantReadiness } from "../shared/readiness";
import { ReadinessChip } from "../shared/StateChip";

// ─────────────────────────────────────────────────────────────────────────────
//  Merchant 360
//
//  This page used to render 18 tabs as one horizontally-scrolling pill strip
//  with arrow buttons -- a scroll affordance on a tab bar is the tell that the
//  information architecture gave up. The tabs also had no grouping and no
//  indication of which ones had anything configured.
//
//  PANELS below keeps the ORIGINAL ORDER AND INDICES on purpose: `?tab=N` deep
//  links and the row menu in MerchantDataTable (which imports TABS from here)
//  address panels by index. Adding `group` to each entry is what buys the
//  two-level nav, without breaking a single existing link.
// ─────────────────────────────────────────────────────────────────────────────

const G = {
    profile:    { key: 'profile',    label: 'Profile',     icon: <BadgeOutlinedIcon />,       blurb: 'Who they are, who owns the account, how onboarding went.' },
    commercials:{ key: 'commercials',label: 'Commercials', icon: <PaymentsOutlinedIcon />,    blurb: 'What they pay, what we collect, what we bill.' },
    fulfilment: { key: 'fulfilment', label: 'Fulfilment',  icon: <Inventory2OutlinedIcon />,  blurb: 'What ships, from where, with which couriers.' },
    allocation: { key: 'allocation', label: 'Allocation',  icon: <RocketLaunchOutlinedIcon />,blurb: 'Which engine and rules decide the courier.' },
    automation: { key: 'automation', label: 'Automation',  icon: <SmartToyOutlinedIcon />,    blurb: 'What the platform does without being asked.' },
};

const GROUP_ORDER = [G.profile, G.commercials, G.fulfilment, G.allocation, G.automation];

// index === position in this array === the `?tab=` value. Do not reorder.
const PANELS = [
    { label: 'Basic Details',                   group: 'profile'     },  // 0
    { label: 'Shipments',                       group: 'fulfilment'  },  // 1
    { label: 'Courier Allocation',              group: 'allocation'  },  // 2
    { label: 'Rate Card',                       group: 'commercials' },  // 3
    { label: 'Notifications',                   group: 'automation'  },  // 4
    { label: 'Notification Pricing',            group: 'commercials' },  // 5
    { label: 'Weight Discrepancy',              group: 'automation'  },  // 6
    { label: 'COD',                             group: 'commercials' },  // 7
    { label: 'Invoice',                         group: 'commercials' },  // 8
    { label: 'Configuration',                   group: 'automation'  },  // 9
    { label: 'Account Team',                    group: 'profile'     },  // 10  (was "Prozo Brand Personnel Mapping")
    // 11: moved to PTL FREIGHT → PTL Merchant Setup → All Merchants → Merchant Update → Allocation. The slot stays so later indices keep
    // their `?tab=` links; it is hidden from the nav and an old link opens the new screen.
    { label: 'Whitelist B2B Couriers',          group: 'fulfilment', movedTo: (id) => `/ptl/merchant-setup/update/${id}/allocation` },  // 11
    { label: 'Lost and Damage',                 group: 'automation'  },  // 12
    { label: 'SKU Master',                      group: 'fulfilment'  },  // 13
    { label: 'Allocation Rules History',        group: 'allocation'  },  // 14
    { label: 'Claims Settings',                 group: 'automation'  },  // 15
    { label: 'Onboarding',                      group: 'profile'     },  // 16
    { label: 'NETRA',                           group: 'automation'  },  // 17
    { label: 'Routing & Pincodes',              group: 'fulfilment'  },  // 18  folded in from Admin
];

// Kept for any `?tab=` link already in the wild, and for callers that only need
// index -> label.
export const TABS = PANELS.map((p) => p.label);

// The same panels, grouped -- so other screens (e.g. the merchant list row menu)
// can offer them by domain instead of as one flat list of 19.
export const PANEL_GROUPS = GROUP_ORDER.map((g) => ({
    key: g.key,
    label: g.label,
    panels: PANELS
        .map((p, index) => ({ ...p, index }))
        .filter((p) => p.group === g.key && !p.movedTo),
}));

const COURIER_ALLOCATION_TAB = 2;

export default function MerchantDetailTabs() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const access = getAccess() || [];
    // A user holding only MERCHANT_COURIER_ALLOCATION (without INTERNAL /
    // MERCHANT_ADMIN) may view ONLY the Courier Allocation panel.
    const allocationOnly =
        access.includes("MERCHANT_COURIER_ALLOCATION") &&
        !access.includes("INTERNAL") &&
        !access.includes("MERCHANT_ADMIN");

    const initialTab = allocationOnly ? COURIER_ALLOCATION_TAB : (Number(searchParams.get('tab')) || 0);
    const [active, setActive] = useState(initialTab);
    const [data, setData] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [reload, setReload] = useState(0);
    const panelRef = useRef(null);

    useEffect(() => {
        if (!id) return;
        setIsLoading(true);
        getGeneric(`${apiConstants.MERCHANT}/${id}`)
            .then((res) => setData(res?.data))
            .catch((err) => console.error(err))
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, reload]);

    const activeGroup = PANELS[active]?.group || 'profile';

    const visibleGroups = useMemo(() => {
        if (allocationOnly) return GROUP_ORDER.filter((g) => g.key === 'allocation');
        return GROUP_ORDER;
    }, [allocationOnly]);

    const panelsIn = (groupKey) => PANELS
        .map((p, i) => ({ ...p, i }))
        .filter((p) => p.group === groupKey && !p.movedTo && (!allocationOnly || p.i === COURIER_ALLOCATION_TAB));

    const goTo = (i) => {
        setActive(i);
        // Keep the URL honest so a panel can be linked and the back button works.
        const next = new URLSearchParams(searchParams);
        next.set('tab', String(i));
        setSearchParams(next, { replace: true });
        panelRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };

    const openGroup = (g) => {
        const first = panelsIn(g.key)[0];
        if (first) goTo(first.i);
    };

    const readiness = merchantReadiness(data);

    return (
        <>
            {isLoading && <Loader />}

            <Box sx={{ mb: 1.5 }}>
                <Button
                    size="small"
                    startIcon={<ArrowBackIcon fontSize="small" />}
                    onClick={() => navigate('/merchant-config/merchants')}
                    sx={{ textTransform: 'none', color: C.inkFaint, '&:hover': { color: C.accent, bgcolor: 'transparent' } }}
                >
                    All merchants
                </Button>
            </Box>

            {/* ── Identity header. Says who this is and what state they are in,
                   before you open anything. ─────────────────────────────────── */}
            <Card elevation={0} sx={{ ...SX.card, mb: 1.5, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ ...TYPE.pageTitle, mb: 0.25 }}>
                            {data?.name || '—'}
                        </Typography>
                        <Typography sx={{ ...TYPE.rowHelp }}>
                            {[data?.companyName, data?.merchantId ? `#${data.merchantId}` : null, data?.city]
                                .filter(Boolean).join(' · ') || 'Loading…'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {data?.isD2CMerchant && (
                            <Box component="span" sx={{ ...SX.pill, color: C.accentInk, bgcolor: C.accentSoft, border: `1px solid ${C.accentBorder}` }}>D2C</Box>
                        )}
                        {data?.isPTLMerchant && (
                            <Box component="span" sx={{ ...SX.pill, color: C.accentInk, bgcolor: C.accentSoft, border: `1px solid ${C.accentBorder}` }}>PTL</Box>
                        )}
                        {data?.isTestMerchant && (
                            <Box component="span" sx={{ ...SX.pill, color: C.warn, bgcolor: C.warnSoft, border: `1px solid ${C.warnBorder}` }}>Test</Box>
                        )}
                        {readiness.map((r) => (
                            <ReadinessChip key={r.key} tone={r.tone} label={r.label} hint={r.hint} />
                        ))}
                    </Box>
                </Box>

                {/* ── Level 1: domain groups ──────────────────────────────── */}
                <Box sx={{
                    display: 'flex', gap: 0.5, px: 1.25, pt: 0.5,
                    bgcolor: C.surfaceSunken, borderTop: `1px solid ${C.lineSoft}`,
                    overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
                }}>
                    {visibleGroups.map((g) => {
                        const on = activeGroup === g.key;
                        return (
                            <Tooltip key={g.key} title={g.blurb} arrow placement="top">
                                <Box
                                    role="tab"
                                    aria-selected={on}
                                    tabIndex={0}
                                    onClick={() => openGroup(g)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openGroup(g); } }}
                                    sx={{
                                        flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                        px: 1.75, py: 1.25,
                                        fontSize: 14,
                                        fontWeight: on ? 700 : 600,
                                        color: on ? C.accent : C.inkBody,
                                        borderBottom: `2px solid ${on ? C.accent : 'transparent'}`,
                                        transition: 'color .15s, border-color .15s',
                                        '& svg': { fontSize: 18 },
                                        '&:hover': { color: on ? C.accent : C.ink },
                                        '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: -2 },
                                    }}
                                >
                                    {g.icon}{g.label}
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Card>

            {/* ── Level 2: panels within the active group ─────────────────── */}
            <Box sx={{ display: 'flex', gap: 0.6, mb: 1.5, flexWrap: 'wrap' }}>
                {panelsIn(activeGroup).map((p) => {
                    const on = active === p.i;
                    return (
                        <Box
                            key={p.i}
                            role="tab"
                            aria-selected={on}
                            tabIndex={0}
                            onClick={() => goTo(p.i)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTo(p.i); } }}
                            sx={{
                                cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                                px: 1.6, py: 0.7, borderRadius: RADIUS.pill,
                                fontSize: 13, fontWeight: on ? 700 : 600,
                                bgcolor: on ? C.accent : C.surface,
                                color: on ? '#fff' : C.inkBody,
                                border: `1px solid ${on ? C.accent : C.lineStrong}`,
                                boxShadow: on ? SHADOW.card : 'none',
                                transition: 'all .15s',
                                '&:hover': { borderColor: C.accent, color: on ? '#fff' : C.accent },
                                '&:focus-visible': { outline: `2px solid ${C.accent}`, outlineOffset: 2 },
                            }}
                        >
                            {p.label}
                        </Box>
                    );
                })}
            </Box>

            {/* ── Panel body ─────────────────────────────────────────────── */}
            <Card ref={panelRef} elevation={0} sx={{ ...SX.card, p: 2.5 }}>
                {active === 0  && <DetailsTab data={data} id={id} setReload={setReload} />}
                {active === 1  && <ShipmentDetails merchant={data?.name} id={id} details={data} />}
                {active === 2  && (
                    <AllocationEnginePanel merchantId={id} merchant={data}>
                        {/* Master rendered Rules + RuleHistory together on this tab;
                            both are kept so nothing moves out of reach. RuleHistory
                            is also its own panel (index 14) for direct linking. */}
                        <Rules data={data} id={id} />
                        <RuleHistory merchantOid={id} />
                    </AllocationEnginePanel>
                )}
                {active === 3  && <RateCard data={data} id={id} />}
                {active === 4  && <NotificationsSubscriptions data={data} id={id} />}
                {active === 5  && <NotificationPricing id={id} merchant={data?.name} />}
                {active === 6  && <AllDiscrepancy merchantId={id} merchantName={data?.name} location="master" />}
                {active === 7  && <Remittance id={id} merchant={data?.name} />}
                {active === 8  && <ViewInvoice id={id} merchant={data?.name} />}
                {active === 9  && <Configs id={id} data={data} setReload={setReload} />}
                {active === 10 && <KamDetails merchant={data?.name} id={id} />}
                {PANELS[active]?.movedTo && <Navigate to={PANELS[active].movedTo(id)} replace />}
                {active === 12 && <ViewLostAndDamageOrders id={id} merchant={data?.name} />}
                {active === 13 && <ViewSku id={id} merchant={data?.name} />}
                {active === 14 && <RuleHistory merchantOid={id} />}
                {active === 15 && <ClaimsSettings id={id} data={data} />}
                {active === 16 && <OnboardingTab id={id} data={data} />}
                {active === 17 && (
                    <NetraConfig
                        useNetra={data?.useNetra}
                        showNetraScoreInMerchantPanel={data?.showNetraScoreInMerchantPanel}
                        merchantId={id}
                        setReload={setReload}
                    />
                )}
                {active === 18 && <RoutingAndPincodes merchantId={id} embedded />}
            </Card>
        </>
    );
}
